import { streemLogger } from '../util/streem_logger';
import { ChimeRemoteVideoTrack } from '../tracks/video_tracks';
import { ChimeRemoteDataTrack } from '../tracks/data_tracks';
import { createVolumeIndicatorCallback } from '../observers/volume_indicator_callback';
import { AWS_ATTENDEE_PREFIX } from '../util/constants';
export class RemoteAttendeeManager {
    constructor(localAttendeeId, audioVideoFacade, onRemoteAudioLevelUpdate, onRemoteSignalStrengthUpdate, onRemoteAttendeeAdded, onRemoteAttendeeRemoved, videoPriorityBasedPolicyManager) {
        this.localAttendeeId = localAttendeeId;
        this.audioVideoFacade = audioVideoFacade;
        this.onRemoteAudioLevelUpdate = onRemoteAudioLevelUpdate;
        this.onRemoteSignalStrengthUpdate = onRemoteSignalStrengthUpdate;
        this.onRemoteAttendeeAdded = onRemoteAttendeeAdded;
        this.onRemoteAttendeeRemoved = onRemoteAttendeeRemoved;
        this.videoPriorityBasedPolicyManager = videoPriorityBasedPolicyManager;
        this.logger = streemLogger.extend('sdk-chime-video:RemoteAttendeeManager');
        this.remoteAttendees = [];
    }
    getOrCreateAttendee(attendeeId, externalUserId) {
        const attendee = this.getAttendeeById(attendeeId);
        if (attendee) {
            this.logger.info(`Found existing attendee with attendeeId: ${attendee.attendeeId} and userId: ${attendee.externalUserId}, no need to create a new one`);
            return attendee;
        }
        else {
            const createdAttendee = this.createNewAttendee(attendeeId, externalUserId);
            this.logger.info(`Attendee with attendeeId: ${attendeeId} and userId: ${externalUserId} not found, new attendee created with attendeeId: ${createdAttendee.attendeeId} and userId: ${createdAttendee.externalUserId}`);
            return createdAttendee;
        }
    }
    removeAttendee(attendeeId) {
        const attendee = this.getAttendeeById(attendeeId);
        if (!attendee) {
            this.logger.warn(`Remote attendee not found for attendeeId: ${attendeeId}, already removed`);
            return;
        }
        this.logger.info(`Removing attendee with attendeeId: ${attendeeId} and userId: ${attendee.externalUserId}`);
        this.disconnectAttendee(attendee);
        this.remoteAttendees = this.remoteAttendees.filter(attendee => attendee.attendeeId !== attendee.attendeeId);
    }
    disconnect() {
        this.remoteAttendees.forEach(attendee => this.disconnectAttendee(attendee));
        this.remoteAttendees = [];
    }
    pauseAttendee(attendee) {
        this.logger.warn(`Remote video tile is paused for userId: ${attendee.externalUserId}.  May indicate insufficient bandwidth`);
        attendee.isPaused = true;
        attendee.videoTrack.onPausedChange(true);
    }
    unpauseAttendee(attendee) {
        this.logger.info(`Remote video tile is unpaused for userId: ${attendee.externalUserId}`);
        attendee.isPaused = false;
        attendee.videoTrack.onPausedChange(false);
    }
    updateEnabledStateForAttendeeVideoTrack(attendee, enabled) {
        this.logger.debug(`Remote video track enabled state changed for userId: ${attendee.externalUserId}`);
        attendee.videoTrack.onEnabledChange(enabled);
    }
    removeTileIdFromAttendee(attendee) {
        attendee.tileId = undefined;
    }
    checkIfAttendeeCanBeIgnored(attendeeId, externalUserId) {
        return (externalUserId?.startsWith(AWS_ATTENDEE_PREFIX) || attendeeId === this.localAttendeeId);
    }
    getAttendeeByTileId(attendeeTileId) {
        return this.remoteAttendees.find(attendee => attendee.tileId === attendeeTileId);
    }
    createNewAttendee(attendeeId, externalUserId) {
        this.logger.info(`Creating new attendee with attendeeId: ${attendeeId} and userId: ${externalUserId}`);
        const volumeIndicatorCallback = createVolumeIndicatorCallback(attendeeId, this.onRemoteAudioLevelUpdate, this.onRemoteSignalStrengthUpdate);
        const videoTrack = new ChimeRemoteVideoTrack(this.audioVideoFacade, externalUserId);
        videoTrack.initializeVideoTrack();
        const newAttendee = {
            attendeeId,
            externalUserId,
            present: true,
            dataTrack: new ChimeRemoteDataTrack(this.audioVideoFacade),
            videoTrack,
            volumeIndicatorCallback,
            isPaused: false,
        };
        this.videoPriorityBasedPolicyManager.addVideoPreference(newAttendee);
        this.audioVideoFacade.realtimeSubscribeToVolumeIndicator(attendeeId, volumeIndicatorCallback);
        this.remoteAttendees.push(newAttendee);
        this.onRemoteAttendeeAdded(newAttendee);
        return newAttendee;
    }
    disconnectAttendee(attendee) {
        attendee.dataTrack.disconnect();
        attendee.videoTrack.disconnect();
        this.videoPriorityBasedPolicyManager.removeVideoPreference(attendee);
        if (attendee.volumeIndicatorCallback) {
            this.audioVideoFacade.realtimeUnsubscribeFromVolumeIndicator(attendee.attendeeId, attendee.volumeIndicatorCallback);
            attendee.volumeIndicatorCallback = undefined;
        }
        this.onRemoteAttendeeRemoved(attendee);
    }
    getAttendeeById(attendeeId) {
        return this.remoteAttendees.find(attendee => attendee.attendeeId === attendeeId);
    }
}
//# sourceMappingURL=remote_attendee_manager.js.map